import React, { useState } from "react";
import {
  Backdrop,
  Button,
  Divider,
  Drawer,
  LogoLoader,
  showToast,
  StatusTags,
} from "../../../../../components";
import ApplicantRejectModal from "./ApplicantRejectModal";
import ResendModal from "./ResendModal";
import VerifyModal from "./VerifyModal";
import ViewModal from "./ViewModal";
import { useEffect } from "react";
import { useLoginUser } from "../../../../../hooks/useLoginUser";
import { getPresignedUrl, uploadFiles } from "../../../../../redux/slices/universal/fileUploadApi";
import { cloneDeep } from "lodash";
import { updateOnboarding } from "../../../../../redux/slices/onboarding/onboardingApi";
import { useCallback } from "react";

const DashboardDrawer = ({
  isDrawerOpen,
  setIsDrawerOpen,
  drawerData,
  childOnboardingsFetch,
}) => {
  if (!isDrawerOpen) return null;
  //if multiple checklists added this api structure will change
  const { userData: loginUserData } = useLoginUser();
  const loginUserUiid = loginUserData?.uiid;
  const [loading, setLoading] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [rejectApplicantModalOpen, setRejectApplicantModalOpen] =
    useState(false);
  const [resendModalOpen, setResendModalOpen] = useState(false);
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  //displaying fake data
  const initialValue = {
    employeeInfo: {
      name: "",
      email: "",
      doj: "",
    },
    cardsData: [],
    checkListName: "",
  };
  // const [onboardingData, setOnboardingData] = useState(dummyData);

  // pending
  // IN Progres to show actual data
  const [onboardingData, setOnboardingData] = useState(initialValue);
  useEffect(() => {
    if (drawerData) {
      setOnboardingData(drawerData);
    }
  }, [drawerData]);

  const handleViewOpener = async (row) => {
    try {
      setLoading(true);

      const docMetadata = row?.metaData?.id;
      const approversArray = row?.metaData?.approver;

      if (!docMetadata?.file) {
        showToast("No File Found!", "error");
        return;
      }

      const presignedUrl = await getPresignedUrl(docMetadata.file);
      const response = await fetch(presignedUrl, {
        mode: "cors",
      });
      const urlDataInBlob = await response.blob();
      const arrayBuffer = await urlDataInBlob.arrayBuffer();

      const documentForPreview = {
        onboardingInfo: row?.onboardingInfo,
        id: docMetadata._id,
        referenceId: docMetadata.templateId,
        documentName: docMetadata.name,
        category: docMetadata.category,
        type: docMetadata.type,
        description: docMetadata.description,
        toBeFilledBy: docMetadata.filledBy,
        createdBy: `User`,
        createdOn: docMetadata.addedOn,
        workflows: [
          {
            approvers: {
              label: docMetadata.approver,
              value: docMetadata.approver,
            },
            initiator: {
              label: docMetadata.initiator,
              value: docMetadata.initiator,
            },
          },
        ],
        arrayBuffer,
        pageCount: docMetadata.pageCount,
        droppedFields: docMetadata.fields || {},
        status: docMetadata.status,
        metaData: docMetadata,
        onboardingDocsApprover: approversArray,
      };
      setCurrentDocument(documentForPreview);
      setViewModalOpen(true);
    } catch (e) {
      console.error(
        "Error in handleViewOpener in src/pages/InnerPages/EOnboardingNew/Dashboard/ModalsAndDrawers/DashboardDrawer.jsx",
        e,
      );
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateField = (fieldId, value, userId = null) => {
    if (!(currentDocument?.onboardingDocsApprover || [])?.includes(userId)) {
      showToast("You are not an Approver to fill details.", "warning", 5000);
      return;
    }
    setCurrentDocument((prevDoc) => {
      if (!prevDoc) return prevDoc;

      // const updatedFields = { ...prevDoc.droppedFields };
      const updatedFields = cloneDeep(prevDoc.droppedFields);
      let fieldFound = false;

      Object.keys(updatedFields).forEach((pageNum) => {
        updatedFields[pageNum] = updatedFields[pageNum].map((field) => {
          if (field.id !== fieldId) return field;

          fieldFound = true;
          if (!field.value || typeof field.value !== "object") {
            field.value = {};
          }
          field.value = value;

          return { ...field };
        });
      });

      if (!fieldFound) {
        console.warn(`Field ${fieldId} not found in document`);
      }

      return {
        ...prevDoc,
        droppedFields: updatedFields,
      };
    });
  };

  const ACTION_CONFIG = {
    reject: {
      status: 4,
      closeModal: setRejectApplicantModalOpen,
      toastMessage: "Document Rejected.",
      type: "error",
    },
    resend: {
      sendEmail: true,
      closeModal: setResendModalOpen,
      toastMessage: "Mail sent successfully.",
      type: "success",
    },
    verify: {
      status: 3,
      closeModal: setVerifyModalOpen,
      toastMessage: "Document Verified.",
      type: "success",
    },
  };

  const buildFieldsPayload = async (droppedFields = {},uiid) => {
    // Object.values(droppedFields).flatMap((fieldList) =>
    //   fieldList.map(({ id, value }) => ({ id, value })),
    // );
    const fields =await Promise.all(
      Object.entries(droppedFields || {}).flatMap(
        ([key, list]) =>
          list?.map(async (item) => {
            try {
              let value = item?.value;
              if (item.type === "image" && typeof value !== "string") {
                const uint8Array = new Uint8Array(value);
                const blob = new Blob([uint8Array]);
                const file = new File([blob], `${item.id}.png`, { type: "image/*" });
                const uploadRes = await uploadFiles({
                  uploadType: "single",
                  field: "doc",
                  uiid,
                  entity: "onboardings",
                  category: "checklist_image",
                  coll: "Onboarding",
                  doc: file,
                });
                const uploadedImageUrl = uploadRes?.data?.data?.url;
                if (!uploadedImageUrl) {
                  throw new Error("Failed to upload image");
                }
                value = uploadedImageUrl;
              }

              return {
                id: item.id,
                value,
                type: item.type,
              };
            } catch (error) {
              console.error("Error uploading image:", error);
              throw error;
            }
          }) || [],
      ),
    );
    return fields
  };

  const buildUpdatePayload = (fields, action = null) => [
    {
      type: "SET",
      value: {
        "checklists.0.documents.$[elem]": {
          fields,
          ...(action && { status: ACTION_CONFIG[action]?.status }),
        },
      },
    },
  ];

  const resetUIState = () => {
    setIsDrawerOpen(false);
    setViewModalOpen(false);
    setCurrentDocument(null);
  };

  const actionsHandler = useCallback(
    async (action) => {
      const actionConfig = ACTION_CONFIG[action];

      if (!actionConfig) {
        console.error(`Unsupported action: ${action}`);
        return;
      }

      try {
        setLoading(true);

        const onboardingId = currentDocument?.onboardingInfo?.onboardingUiid;
        const documentEditingId =
          currentDocument?.onboardingInfo?.currentEditingDoc;

        if (!onboardingId || !documentEditingId) {
          throw new Error(
            "Onboarding ID or current document editing ID is missing.",
          );
        }

        const fields = await buildFieldsPayload(currentDocument?.droppedFields,onboardingId);
        const payload = buildUpdatePayload(fields, action);

        const response = await updateOnboarding({
          coll: "Onboarding",
          uiid: onboardingId,
          payload,
          patchId: documentEditingId,
          ...(actionConfig?.sendEmail && {
            postActions: ["CheckListSendEmail"],
          }),
        });

        if (!response?.success) {
          throw new Error("Failed to update onboarding document.");
        }

        childOnboardingsFetch();
        showToast(actionConfig.toastMessage, actionConfig.type, 10000);
        actionConfig.closeModal(false);
        resetUIState();
      } catch (error) {
        console.error(error?.message || "Unexpected error", error);
      } finally {
        setLoading(false);
      }
    },
    [currentDocument],
  );

  return (
    <div>
      <Drawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        position="right"
        width="w-[60%]"
        zIndex={"40"}
        showCloseButton={false}
        bodyGap={false}
        closeOnOutsideClick={true}
      >
        <div className="flex flex-col flex-1 gap-6">
          {/* Top Header */}
          <div className="flex items-center justify-between bg-primary-50">
            <div className="flex items-start justify-start px-2 py-4">
              <Button
                icon={"person"}
                variant="text"
                type="icon"
                size="xl"
                color="primary"
              />
              <div>
                <div className="font-semibold text-lg">
                  {onboardingData?.employeeInfo?.name}
                </div>
                <span className="text-sm text-gray-600">
                  {onboardingData?.employeeInfo?.email}
                </span>{" "}
                <span className="text-primary-500">|</span>{" "}
                <span className="text-xs text-neutrals-400 mt-1">
                  DOJ{" "}
                  <span className="text-gray-800">{onboardingData?.doj}</span>
                </span>
              </div>
            </div>
            <div className=" mr-3">
              <Button
                type="icon"
                variant="text"
                color="black"
                onClick={() => setIsDrawerOpen(false)}
                icon={"close"}
              />
            </div>
          </div>

          {/* Checklist Title */}
          <div className=" text-primary-600 pb-2 pl-3">
            Checklist Name: {onboardingData?.checkListName}
          </div>

          {/* Cards List */}
          <div className="flex flex-col gap-4 overflow-auto shadow-lg rounded mx-3 ">
            {onboardingData?.cardsData?.map((card, index) => (
              <div
                key={index}
                className="
    px-6 py-2 border-b border-b-gray-300

    grid grid-cols-[40px_minmax(200px,1fr)_100px_100px]
    items-center gap-4 w-full
  "
              >
                <div className="flex justify-start">
                  <Button
                    icon="picture_as_pdf"
                    variant="text"
                    size="xl"
                    color="black"
                    className="!px-2"
                  />
                </div>

                <div>
                  <div className="font-medium truncate">{card.title}</div>

                  <div className="text-sm text-gray-600 mt-2">
                    <span className="text-neutrals-400">Initiated Date: </span>
                    <span className="font-semibold">
                      {card.initiatedDate}
                    </span>{" "}
                    <span className="text-primary-500">|</span>{" "}
                    <span className="text-neutrals-400">Due Date: </span>
                    <span className="font-semibold">{card.dueDate}</span>
                  </div>
                </div>

                <StatusTags
                  tag={card?.status?.label}
                  className={`px-3 py-1 text-xs rounded text-center w-fit
                    bg-${card?.status?.color}-100 text-${card?.status?.color}-600`}
                  size="sm"
                />

                {/* View Button (same place for every card) */}
                <div className="flex justify-end">
                  <Button
                    icon="visibility"
                    title="View"
                    size="sm"
                    className="mr-2"
                    onClick={() => handleViewOpener(card)}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </Drawer>
      <ViewModal
        document={currentDocument}
        setCurrentDocument={setCurrentDocument}
        onUpdateField={handleUpdateField}
        viewModalOpen={viewModalOpen}
        setViewModalOpen={setViewModalOpen}
        setRejectApplicantModalOpen={setRejectApplicantModalOpen}
        setVerifyModalOpen={setVerifyModalOpen}
        setResendModalOpen={setResendModalOpen}
        loginUserUiid={loginUserUiid}
      />
      <ApplicantRejectModal
        rejectApplicantModalOpen={rejectApplicantModalOpen}
        setRejectApplicantModalOpen={setRejectApplicantModalOpen}
        setViewModalOpen={setViewModalOpen}
        loginUserUiid={loginUserUiid}
        actionsHandler={actionsHandler}
      />
      <ResendModal
        resendModalOpen={resendModalOpen}
        setResendModalOpen={setResendModalOpen}
        setViewModalOpen={setViewModalOpen}
        loginUserUiid={loginUserUiid}
        actionsHandler={actionsHandler}
      />

      <VerifyModal
        verifyModalOpen={verifyModalOpen}
        setVerifyModalOpen={setVerifyModalOpen}
        setViewModalOpen={setViewModalOpen}
        loginUserUiid={loginUserUiid}
        actionsHandler={actionsHandler}
      />
      <Backdrop isOpen={loading}>
        <LogoLoader />
      </Backdrop>
    </div>
  );
};

export default DashboardDrawer;
